import React, { useEffect } from 'react';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  SingleSelectionRadio,
  Typography,
} from 'web-library';



import { makeStyles } from 'tss-react/mui';
import { TFunction, useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { useHistory } from 'react-router-dom';
import { MutationStatus } from 'react-query';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import ControlledAutocomplete from '../../../components/form-items/ControlledAutocomplete';
import {
  ExamSeriesModel,
  useGetExamSeries,
} from '../../../api-refactored/special-consideration/useGetExamSeries';
import {
  ExamLevelModel,
  usePostExamLevels,
} from '../../../api-refactored/special-consideration/usePostExamLevels';
import RouteKey from '../../../routes/key';
import SpecialConsiderationGetStarted from '../Listing/schema/SpecialConsiderationGetStarted';
import {
  ExamLevelSeriesRequest,
  SpecialConsiderationEligibilityResponse,
  usePostCheckEligibility,
} from '../../../api-refactored/special-consideration/usePostCheckEligibility';
import { AddCandidateMode } from './SpecialConsiderationCoordinatorListingTable';

const { specialConsideration: specialConsiderationRoute } = RouteKey.child;
const { specialConsideration, instructions } = RouteKey['special-consideration'].child;

const useStyles = makeStyles()((theme) =>
({
  customClass: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  buttonContainer: {
    marginTop: theme.spacing(3),
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  controlledAutoComplete: {
    paddingTop: theme.spacing(3),
  },
  keyLabel: {
    color: theme.color.text.textHeader,
    fontWeight: 700,
  },
  requiredLabel: {
    color: theme.color.state.stateImportant,
    marginLeft: theme.spacing(1),
    display: 'inline',
  },
  errorText: {
    marginTop: theme.spacing(3),
    color: theme.color.state.stateError,
  },
  addCandidatesModeContainer: {
    marginTop: theme.spacing(3),
  },
  additionalLabel: {
    color: theme.color.text.textTertiary,
    fontSize: '16px',
    fontWeight: 700,
    display: 'inline-block',
    position: 'relative',
    top: '1.25px',
  },
  selectedRadioButton: {
    pointerEvents: 'none',
  },
  disabledRadioButton: {
    color: theme.color.text.textDisabled,
    pointerEvents: 'none',
  },
}));

const RadioComponentForPSLE = ({
  addCandidateMode,
  t,
  classes,
}: {
  addCandidateMode: AddCandidateMode;
  t: TFunction<'translation'>;
  classes: Record<string,string>;
}) => (
  <RadioGroup value={addCandidateMode}>
    <Box className={classes.selectedRadioButton}>
      <FormControlLabel
        key="form-control-label-psle"
        value={AddCandidateMode.applyForIndividual}
        control={<Radio color="primary" />}
        label={t('specialConsideration->>SelectExamLevelModal.ApplyForIndividual')}
        checked
      />
    </Box>
    <Box className={classes.disabledRadioButton}>
      <FormControlLabel
        key="form-control-label-disabled"
        value={AddCandidateMode.applyForGroup}
        control={<Radio color="primary" classes={{ root: classes.disabledRadioButton }} />}
        label={t('specialConsideration->>SelectExamLevelModal.ApplyForGroup')}
      />
      <Box key="additionalLabel-disabled" className={classes.additionalLabel}>
        {t('specialConsideration->>SelectExamLevelModal.FiveCandidatesAndAbove')}
      </Box>
    </Box>
  </RadioGroup>
);

interface SelectExamLevelModalProps {
  modalState: boolean;
  onDialogClose: () => void;
  examLevel: string | null;
  examSeries: string | null;
  isError: boolean;
  addCandidateMode: AddCandidateMode;
  dialogCustomStatus: MutationStatus;
  examSeriesPSLEObject: ExamSeriesModel | undefined;
  isPSLE: boolean;
  setExamLevel: React.Dispatch<React.SetStateAction<string | null>>;
  setExamSeries: React.Dispatch<React.SetStateAction<string | null>>;
  setIsError: React.Dispatch<React.SetStateAction<boolean>>;
  setAddCandidateMode: React.Dispatch<React.SetStateAction<AddCandidateMode>>;
  setDialogCustomStatus: React.Dispatch<React.SetStateAction<MutationStatus>>;
  setExamSeriesPSLEObject: React.Dispatch<React.SetStateAction<ExamSeriesModel | undefined>>;
  setIsPSLE: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface ScInstructionInfo {
  isContinueApplication: boolean;
  examLevelUuid?: string;
  examSeriesUuid?: string;
  addCandidateMode?: AddCandidateMode;
  applicationUuid?: string;
}

const SelectExamLevelModal = ({
  modalState,
  onDialogClose,
  examLevel,
  examSeries,
  isError,
  addCandidateMode,
  dialogCustomStatus,
  examSeriesPSLEObject,
  isPSLE,
  setExamLevel,
  setExamSeries,
  setIsError,
  setAddCandidateMode,
  setDialogCustomStatus,
  setExamSeriesPSLEObject,
  setIsPSLE,
}: SelectExamLevelModalProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();

  const formHook = useForm<ExamLevelSeriesRequest>({
    mode: 'onSubmit',
    resolver: joiResolver(SpecialConsiderationGetStarted()),
  });

  const { control } = formHook;

  const { data: examLevelData, mutate: mutatePostExamLevels } = usePostExamLevels();

  const { data: examSeriesData } = useGetExamSeries();

  const {
    data: checkEligibilityData,
    mutate: mutatePostCheckEligibility,
  } = usePostCheckEligibility({
    onSuccess: (data: SpecialConsiderationEligibilityResponse) => {
      if (!data.eligible) {
        setIsError(true);
        setDialogCustomStatus('error');
      } else if (examLevel && (examSeriesPSLEObject || examSeries)) {
        const scInstructionInfo: ScInstructionInfo = {
          examLevelUuid: examLevel,
          examSeriesUuid:
            isPSLE && examSeriesPSLEObject ? examSeriesPSLEObject.code : examSeries || '',
          addCandidateMode,
          isContinueApplication: false,
        };
        setDialogCustomStatus('success');
        history.push(
          `/${specialConsiderationRoute}/${specialConsideration.route}/${instructions.route}`,
          scInstructionInfo
        );
      }
    },
  });

  useEffect(
    () =>
      mutatePostExamLevels({
        codeType: 'APPL_EXAM_LVL_CD',
        codeInList: ['GCEA', 'GCEO', 'GCEN', 'PSLE'],
      }),
    [mutatePostExamLevels]
  );

  useEffect(() => {
    if (isPSLE) {
      setExamSeriesPSLEObject(
        examSeriesData.find((val: ExamSeriesModel) => val.description === 'YEAR-END')
      );
      setAddCandidateMode(AddCandidateMode.applyForIndividual);
    } else {
      setExamSeriesPSLEObject(undefined);
      setAddCandidateMode(AddCandidateMode.applyForNone);
    }
  }, [examSeriesData, isPSLE, setAddCandidateMode, setExamSeriesPSLEObject]);

  return (
    <Dialog open={modalState} onClose={onDialogClose} maxWidth="lg">
      <DialogTitle>{t('specialConsideration->>SelectExamLevelModal.GetStarted')}</DialogTitle>
      <DialogContent>
        <>{t('specialConsideration->>SelectExamLevelModal.Content')}</>
        <Box className={classes.controlledAutoComplete}>
          {examLevelData && (
            <ControlledAutocomplete<ExamLevelModel, false, false, undefined>
              label={t('specialConsideration->>SelectExamLevelModal.ExamLevel')}
              isRequired
              control={control}
              name="examLevel"
              id="input-select-special-consideration-exam-level"
              error={isError}
              options={examLevelData}
              getOptionLabel={(option: ExamLevelModel) => option.description}
              placeholder={t('specialConsideration->>SelectExamLevelModal.SelectExamLevel')}
              transform={{
                input: (selectedOption, options) =>
                  options.find((option) => option.code === selectedOption) ?? null,
                output: (selectedOption) => {
                  const selectedExamLevel = selectedOption ? selectedOption.code : null;
                  if (selectedOption && selectedOption.description === 'PSLE') {
                    setIsPSLE(true);
                  } else {
                    setIsPSLE(false);
                  }
                  setExamLevel(selectedExamLevel);
                  return selectedExamLevel;
                },
              }}
              readOnly
            />
          )}
        </Box>
        <Box className={classes.controlledAutoComplete}>
          {examSeriesData &&
            ((isPSLE && examSeriesPSLEObject) ||
              (!isPSLE && examSeriesPSLEObject === undefined)) && (
              <ControlledAutocomplete<ExamSeriesModel, false, false, undefined>
                label={t('specialConsideration->>SelectExamLevelModal.ExamSeries')}
                isRequired
                control={control}
                name="examSeries"
                id="input-select-special-consideration-exam-series"
                error={isError && examLevel != null && examSeries != null}
                options={examSeriesData}
                getOptionLabel={(option: ExamSeriesModel) => option.description}
                disabled={isPSLE}
                value={examSeriesPSLEObject}
                placeholder={t('specialConsideration->>SelectExamLevelModal.SelectExamSeries')}
                transform={{
                  input: (selectedOption, options) =>
                    options.find((option) => option.code === selectedOption) ?? null,
                  output: (selectedOption) => {
                    const selectedExamSeries = selectedOption ? selectedOption.code : null;
                    if (selectedOption) {
                      setExamSeries(selectedExamSeries);
                    }
                    return selectedExamSeries;
                  },
                }}
                readOnly
              />
            )}
        </Box>
        {isError && (
          <Typography className={classes.errorText}>
            {checkEligibilityData?.error.message}
          </Typography>
        )}
        <Box className={classes.addCandidatesModeContainer}>
          <Typography className={classes.keyLabel} variant="body2">
            {t('specialConsideration->>SelectExamLevelModal.ModeOfAddingCandidates')}
            <Box className={classes.requiredLabel}>*</Box>
          </Typography>
          {isPSLE ? (
            <RadioComponentForPSLE addCandidateMode={addCandidateMode} t={t} classes={classes} />
          ) : (
            <SingleSelectionRadio
              name="single-radio-select-application-type"
              id="single-radio-select-application-type"
              data={[
                {
                  label: t('specialConsideration->>SelectExamLevelModal.ApplyForIndividual'),
                  value: AddCandidateMode.applyForIndividual,
                },
                {
                  label: t('specialConsideration->>SelectExamLevelModal.ApplyForGroup'),
                  additionalLabel: t(
                    'specialConsideration->>SelectExamLevelModal.FiveCandidatesAndAbove'
                  ),
                  value: AddCandidateMode.applyForGroup,
                },
              ]}
              additionalLabelClassName={classes.additionalLabel}
              onChange={(event) => setAddCandidateMode(event.target.value as AddCandidateMode)}
              value={addCandidateMode}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Box className={classes.buttonContainer}>
          <Button id="button-cancel" onClick={onDialogClose} variant="text">
            {t('specialConsideration->>SelectExamLevelModal.Cancel')}
          </Button>
          <ApiButton
            id="button-proceed"
            disabled={!examLevel || (!examSeries && !isPSLE) || (!addCandidateMode && !isPSLE)}
            onClick={() => {
              mutatePostCheckEligibility({
                examLevelUuid: examLevel || '',
                examSeriesUuid:
                  isPSLE && examSeriesPSLEObject ? examSeriesPSLEObject.code : examSeries || '',
                groupScType:
                  addCandidateMode === AddCandidateMode.applyForIndividual ? 'IND' : 'GROUP',
              });
              setDialogCustomStatus('loading');
            }}
            type="submit"
            status={dialogCustomStatus}
          >
            {t('specialConsideration->>SelectExamLevelModal.Proceed')}
          </ApiButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
export default SelectExamLevelModal;
